// src/components/common/LocationAutocomplete.js

import React, { useState, useEffect, useRef } from 'react';

const LocationAutocomplete = ({ 
  value, 
  onChange, 
  onSelect,
  placeholder = "Address, hotel, city...",
  label,
  className = "",
  dark = false
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    // Load Google Maps script if not already loaded
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&language=en`;
      script.async = true;
      script.defer = true;
      script.onload = initAutocomplete;
      document.head.appendChild(script);
    } else {
      initAutocomplete();
    }
  }, []);

  const initAutocomplete = () => {
    if (!inputRef.current || !window.google) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'lk' },
      fields: ['formatted_address', 'geometry', 'name', 'place_id', 'types', 'address_components'],
      language: 'en' // Force English results
    });

    autocompleteRef.current.addListener('place_changed', handlePlaceSelect);
  };

  const getDisplayName = (place) => {
    // Extract city name from address components - use short_name for English
    const getCityName = () => {
      if (!place.address_components) return null;
      
      const cityComponent = place.address_components.find(component => 
        component.types.includes('locality') || 
        component.types.includes('administrative_area_level_2')
      );
      
      // Use short_name which is typically in English
      return cityComponent ? cityComponent.short_name : null;
    };

    // If place has a name (like "Hilton Hotel"), combine it with city
    if (place.name && place.name !== place.formatted_address) {
      const city = getCityName();
      if (city && !place.name.includes(city)) {
        return `${place.name} - ${city}`;
      }
      return place.name;
    }

    // Otherwise, try to create a short version from formatted_address
    // Remove country name and postal codes
    let shortAddress = place.formatted_address
      .replace(/, Sri Lanka$/i, '')
      .replace(/\d{5}/g, '')  // Remove postal codes
      .replace(/,\s*,/g, ',') // Remove double commas
      .trim();

    // Remove any Sinhala/Tamil characters (keep only Latin alphabet, numbers, spaces, and common punctuation)
    shortAddress = shortAddress.replace(/[^\u0000-\u007F\u0080-\u00FF]/g, '').trim();

    // If still too long, take first 2-3 parts
    const parts = shortAddress.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length > 3) {
      shortAddress = parts.slice(0, 3).join(', ');
    }

    return shortAddress || place.formatted_address;
  };

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();
    
    if (!place.geometry) {
      console.error('No geometry for selected place');
      return;
    }

    // Get a short, user-friendly display name in English
    const displayName = getDisplayName(place);
    
    setInputValue(displayName);
    onChange(displayName);

    if (onSelect) {
      onSelect({
        address: place.formatted_address,  // Full address for API
        displayName: displayName,           // Short name for display (English only)
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        placeId: place.place_id,
        name: place.name,
        types: place.types
      });
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  const labelClasses = dark 
    ? "block text-xs font-medium text-gray-400 mb-1" 
    : "block text-sm font-medium text-gray-700 mb-2";

  const inputClasses = dark
    ? `w-full pl-10 pr-4 py-3 border border-gray-700 rounded-lg bg-gray-900 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm ${className}`
    : `w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black transition-all text-base ${className}`;

  return (
    <div className="relative">
      {label && (
        <label className={labelClasses}>
          {label}
        </label>
      )}
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          📍
        </div>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={inputClasses}
          autoComplete="off"
        />
      </div>
    </div>
  );
};

export default LocationAutocomplete;